<template>
  <section v-if="hasEnoughData || $isPageEditing" class="related-content">
    <div class="container">
      <div class="row">
        <div class="col-lg-8 offset-lg-2">
          <h3 class="font-l related-content__title">{{ t('related-content-title') }}</h3>
          <ul
            ref="linksContainer"
            class="related-content__wrapper"
            :class="{ 'related-content__wrapper--expanded': isExpanded }"
          >
            <li
              v-for="(link, index) in props.fields?.links?.value ?? []"
              :key="`${index}-${link?.text || ''}`"
              class="related-content-item"
            >
              <base-link
                classes="related-content-item__link link no-underline"
                :field="{ value: link }"
                :tab-index="
                  !isExpanded && hiddenFromIndex >= 0 && index >= hiddenFromIndex ? -1 : null
                "
                @click="handleLinkClick(link)"
              />
            </li>
          </ul>
          <button
            v-if="canToggle"
            class="related-content__toggle link"
            type="button"
            :aria-expanded="isExpanded"
            @click="toggleExpanded"
          >
            {{ isExpanded ? t('related-content-view-less') : t('related-content-view-all') }}
            <i :class="['icon', isExpanded ? 'icon-Up' : 'icon-Down']" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useButtonsAndLinksTracking } from '../../data-layer/hooks/useButtonsAndLinksTracking';
import { getLinkTrackingData } from '../../data-layer/helpers/utils';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useThrottleFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import BaseLink from '../2_elements/BaseLink/BaseLink.vue';
import useMedia from '../../utils/useMedia';
import type { LinkValue } from '@/types';
import type { RelatedContentProps } from './types';

const props = withDefaults(defineProps<RelatedContentProps>(), {
  fields: () => ({}),
});

const { t } = useI18n();

const hasEnoughData = computed(() => (props.fields?.links?.value?.length ?? 0) > 0);
const canToggle = computed(() => hasEnoughData.value && (hasOverflow.value || isExpanded.value));

const linksContainer = useTemplateRef('linksContainer');
const isExpanded = ref(false);
const hasOverflow = ref(false);
const hiddenFromIndex = ref(-1);
const { isMedia: isDesktop } = useMedia('(min-width: 992px)');
let resizeObserver: ResizeObserver | null = null;

const { trackLinkState } = useButtonsAndLinksTracking({
  componentName: 'related_content',
});

const handleLinkClick = (link: LinkValue | undefined) => {
  if (!link?.href || !link?.text) return;

  const { link_page_path, link_domain } = getLinkTrackingData(link.href);
  const isExternalLink = link?.linktype !== 'internal';

  trackLinkState(link.text, 'navigation_interaction', {
    link_text: link.text,
    link_page_path,
    ...(isExternalLink ? { link_domain } : {}),
  });
};

const measureOverflow = () => {
  if (!linksContainer.value) return;

  if (isExpanded.value) {
    hiddenFromIndex.value = -1;
    linksContainer.value.style.removeProperty('--related-content-visible-height');
    return;
  }

  const items = Array.from(linksContainer.value.children) as HTMLElement[];
  if (!items.length) {
    hasOverflow.value = false;
    hiddenFromIndex.value = -1;
    return;
  }

  const ROW_TOLERANCE = 2;
  const WRAPPER_PADDING = 8;
  const maxVisibleRows = isDesktop.value ? 1 : 2;

  const containerRect = linksContainer.value.getBoundingClientRect();
  const rows: { top: number; bottom: number; firstIndex: number }[] = [];

  for (let i = 0; i < items.length; i++) {
    const itemRect = items[i].getBoundingClientRect();
    const relTop = Math.round(itemRect.top - containerRect.top);
    const relBottom = Math.round(itemRect.bottom - containerRect.top);

    const existingRow = rows.find((r) => Math.abs(r.top - relTop) < ROW_TOLERANCE);
    if (existingRow) existingRow.bottom = Math.max(existingRow.bottom, relBottom);
    else rows.push({ top: relTop, bottom: relBottom, firstIndex: i });
  }

  rows.sort((a, b) => a.top - b.top);

  hasOverflow.value = rows.length > maxVisibleRows;

  if (hasOverflow.value) {
    hiddenFromIndex.value = rows[maxVisibleRows].firstIndex;
    const visibleHeight = rows[maxVisibleRows - 1].bottom + WRAPPER_PADDING;
    linksContainer.value.style.setProperty(
      '--related-content-visible-height',
      `${visibleHeight}px`
    );
  } else {
    hiddenFromIndex.value = -1;
    linksContainer.value.style.removeProperty('--related-content-visible-height');
  }
};

const throttledMeasureOverflow = useThrottleFn(measureOverflow, 50);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;

  if (!isExpanded.value) {
    nextTick(throttledMeasureOverflow);
  }
};

watch(
  () => props.fields?.links?.value?.length ?? 0,
  () => {
    isExpanded.value = false;
    nextTick(throttledMeasureOverflow);
  }
);

onMounted(() => {
  nextTick(() => {
    throttledMeasureOverflow();
    resizeObserver = new ResizeObserver(throttledMeasureOverflow);

    if (linksContainer.value) {
      resizeObserver.observe(linksContainer.value);
    }
  });
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});
</script>

<style lang="scss">
@import './scss/RelatedContent.scss';
</style>
